import { useEffect, useState } from "react";
import { Save, Loader2, Info, Moon, ServerCog } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { apiGet, apiPatch, getMaintenance, setMaintenance } from "@/admin/adminApi";
import { MOCK } from "@/admin/mock";
import { useAdmin } from "@/admin/AdminLayout";
import { PageHeader, Loading } from "@/admin/adminComponents";

const LS_KEY = "halalgo_ramadan_settings";
const MAINT_LS = "halalgo_maintenance";

const TOGGLES = [
  { key: "ramadan_mode_active", label: "Ramadan Mode", desc: "Enable the Ramadan experience across the customer app." },
  { key: "suhoor_delivery_enabled", label: "Suhoor Delivery", desc: "Allow pre-dawn Suhoor delivery windows." },
  { key: "iftar_surge_pricing", label: "Iftar Surge Pricing", desc: "Apply dynamic surge pricing during peak Iftar hours." },
];

const MaintenancePill = ({ on, loading }) => {
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-halalgo-kraft-200 bg-halalgo-paper2 px-2.5 py-1 text-xs font-semibold text-halalgo-ink/55" data-testid="settings-maintenance-status">
        <Loader2 className="h-3 w-3 animate-spin" /> Checking&hellip;
      </span>
    );
  }
  return on ? (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-amber-700" data-testid="settings-maintenance-status">
      <span className="h-2 w-2 rounded-full bg-amber-500" /> Under Maintenance
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-halalgo-green-200 bg-halalgo-green-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-halalgo-green-800" data-testid="settings-maintenance-status">
      <span className="h-2 w-2 rounded-full bg-halalgo-green-600" /> Platform Live
    </span>
  );
};

export default function Settings() {
  const { token } = useAdmin();

  // ---- Maintenance mode (local backend, always functional) ----
  const [maint, setMaint] = useState(null);
  const [maintLoading, setMaintLoading] = useState(true);
  const [maintSaving, setMaintSaving] = useState(false);

  // ---- Ramadan settings (external API with mock fallback) ----
  const [form, setForm] = useState(null);
  const [mocked, setMocked] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getMaintenance();
        setMaint(res);
        localStorage.setItem(MAINT_LS, JSON.stringify(res));
      } catch (e) {
        const local = localStorage.getItem(MAINT_LS);
        setMaint(local ? JSON.parse(local) : { maintenance_mode: false });
      } finally {
        setMaintLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const { data, mocked } = await apiGet("/settings/ramadan", MOCK.ramadan);
      const localRaw = localStorage.getItem(LS_KEY);
      const merged = localRaw ? { ...data, ...JSON.parse(localRaw) } : data;
      setForm(merged);
      setMocked(mocked);
      setLoading(false);
    })();
  }, []);

  const toggleMaintenance = async (val) => {
    setMaintSaving(true);
    setMaint((m) => ({ ...(m || {}), maintenance_mode: val }));
    try {
      const res = await setMaintenance({ maintenance_mode: val }, token);
      setMaint(res);
      localStorage.setItem(MAINT_LS, JSON.stringify(res));
      toast.success(
        val
          ? "Maintenance Mode is ON — the public site now shows the maintenance page."
          : "Maintenance Mode is OFF — the public site is live again."
      );
    } catch (e) {
      const local = { ...(maint || {}), maintenance_mode: val };
      localStorage.setItem(MAINT_LS, JSON.stringify(local));
      setMaint(local);
      toast.warning("Couldn't reach the server — saved locally so it still applies on this device.");
    } finally {
      setMaintSaving(false);
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    const { mocked: patchMocked } = await apiPatch("/settings/ramadan", form, form);
    if (patchMocked) {
      localStorage.setItem(LS_KEY, JSON.stringify(form));
      toast.success("Saved locally (offline mode). Changes will sync when the live API is available.");
    } else {
      localStorage.removeItem(LS_KEY);
      toast.success("Ramadan settings saved to the server.");
    }
    setSaving(false);
  };

  const maintOn = !!maint?.maintenance_mode;

  return (
    <div data-testid="admin-settings-page">
      <PageHeader
        title="Settings"
        subtitle="Manage platform availability and seasonal operations."
        mocked={mocked}
        actions={
          <Button onClick={save} disabled={saving || loading} data-testid="settings-save-button" className="bg-halalgo-green-600 text-white hover:bg-halalgo-green-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </Button>
        }
      />

      <div className="max-w-2xl space-y-6">
        {/* System Maintenance — prominent, always available */}
        <div className="overflow-hidden rounded-xl border border-halalgo-kraft-100 bg-white shadow-lift-sm">
          <div className="flex items-center gap-3 border-b border-halalgo-kraft-100 px-5 py-4">
            <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${maintOn ? "bg-amber-50 text-amber-600" : "bg-halalgo-green-50 text-halalgo-green-700"}`}>
              <ServerCog className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-base font-bold text-halalgo-ink">System Maintenance</h2>
              <p className="text-xs text-halalgo-ink/55">Take the public website offline for scheduled maintenance. Admin access is never affected.</p>
            </div>
          </div>

          <div className="flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <Label htmlFor="maintenance_mode" className="text-sm font-medium text-halalgo-ink">Maintenance Mode</Label>
              <MaintenancePill on={maintOn} loading={maintLoading} />
            </div>
            <div className="flex items-center gap-3">
              {maintSaving ? <Loader2 className="h-4 w-4 animate-spin text-halalgo-ink/50" /> : null}
              <Switch
                id="maintenance_mode"
                checked={maintOn}
                onCheckedChange={toggleMaintenance}
                disabled={maintLoading || maintSaving}
                data-testid="settings-maintenance-toggle"
                className="data-[state=checked]:bg-amber-500"
              />
            </div>
          </div>

          {maintOn ? (
            <div className="flex items-start gap-2 border-t border-amber-100 bg-amber-50/70 px-5 py-3 text-sm text-amber-800" data-testid="settings-maintenance-warning">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>The public website (/, /download, /blog, /contact) is currently showing the maintenance page to visitors.</span>
            </div>
          ) : null}
        </div>

        {/* Ramadan operations */}
        {loading || !form ? (
          <Loading label="Loading Ramadan settings..." />
        ) : (
          <>
            {mocked ? (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="settings-offline-notice">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Live API unavailable. Your Ramadan changes are saved to this browser so the dashboard stays functional, and will sync once the backend is live.</span>
              </div>
            ) : null}

            <div className="rounded-xl border border-halalgo-kraft-100 bg-white shadow-lift-sm">
              <div className="flex items-center gap-3 border-b border-halalgo-kraft-100 px-5 py-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-halalgo-green-50 text-halalgo-green-700">
                  <Moon className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-base font-bold text-halalgo-ink">Ramadan Operations</h2>
                  <p className="text-xs text-halalgo-ink/55">Seasonal toggles for the holy month.</p>
                </div>
              </div>
              <div className="divide-y divide-halalgo-kraft-100">
                {TOGGLES.map((t) => (
                  <div key={t.key} className="flex items-center justify-between gap-4 px-5 py-4">
                    <div>
                      <Label htmlFor={t.key} className="text-sm font-medium text-halalgo-ink">{t.label}</Label>
                      <p className="mt-0.5 text-xs text-halalgo-ink/55">{t.desc}</p>
                    </div>
                    <Switch
                      id={t.key}
                      checked={!!form[t.key]}
                      onCheckedChange={(v) => setField(t.key, v)}
                      data-testid={`settings-toggle-${t.key}`}
                      className="data-[state=checked]:bg-halalgo-green-600"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-halalgo-kraft-100 bg-white p-5 shadow-lift-sm">
              <Label htmlFor="announcement_banner" className="text-sm font-medium text-halalgo-ink">Announcement Banner</Label>
              <p className="mb-2 mt-0.5 text-xs text-halalgo-ink/55">Shown to customers during Ramadan Mode.</p>
              <Textarea
                id="announcement_banner"
                value={form.announcement_banner || ""}
                onChange={(e) => setField("announcement_banner", e.target.value)}
                data-testid="settings-announcement-input"
                rows={3}
                placeholder="e.g. Prepare for Iftar! Order 1 hour ahead."
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
